import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const savedUser = localStorage.getItem('cyberguard_user');
      const savedLoginTime = localStorage.getItem('cyberguard_login_time');
      const savedToken = localStorage.getItem('token');
      
      if (savedUser && savedLoginTime && savedToken) {
        const user = JSON.parse(savedUser);
        const loginTime = parseInt(savedLoginTime);
        const currentTime = Date.now();
        const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
        
        // Check if session is still valid
        if (currentTime - loginTime < sessionDuration) {
          setUser(user);
          setIsAuthenticated(true);
          console.log('✅ Authentication restored from localStorage');
        } else {
          // Session expired
          logout();
          console.log('⚠️ Session expired - cleared authentication');
        }
      }
    } catch (error) {
      console.error('❌ Error checking auth status:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login for:', email);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';

      // Check if this is an admin login attempt
      // Admin can login with: admin@cyberguardglobal.com, team@cyberguardglobal.com, or just "admin"
      const isAdminEmail = email === 'admin' ||
                           email === 'admin@cyberguardglobal.com' ||
                           email === 'team@cyberguardglobal.com' ||
                           email.includes('@cyberguard-admin');

      if (isAdminEmail) {
        console.log('🔐 Attempting admin login');
        const adminResponse = await fetch(`${baseUrl}/admin/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: 'admin', password }),
        });

        if (adminResponse.ok) {
          const data = await adminResponse.json();

          // Extract admin data
          const userData = {
            email: 'admin@cyberguardglobal.com',
            subscription_tier: 'admin',
            name: 'Admin',
            role: 'admin',
            subscription: { plan_id: 'admin' }
          };

          // Store user data and JWT tokens
          localStorage.setItem('cyberguard_user', JSON.stringify(userData));
          localStorage.setItem('cyberguard_login_time', Date.now().toString());
          localStorage.setItem('token', data.access_token); // Admin login returns access_token directly
          localStorage.setItem('refresh_token', data.refresh_token); // Store refresh token

          setUser(userData);
          setIsAuthenticated(true);

          console.log('✅ Admin login successful');
          return { success: true, user: userData };
        } else {
          const error = await adminResponse.json();
          console.error('❌ Admin login failed:', error.detail);
          return { success: false, error: error.detail || 'Admin login failed' };
        }
      }

      // Fall back to regular user login
      const response = await fetch(`${baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();

        // Extract user data and tokens from response
        const userData = {
          email: data.user.email,
          subscription_tier: data.user.subscription_tier,
          name: data.user.email.split('@')[0],
          subscription: { plan_id: data.user.subscription_tier }
        };

        // Store user data and JWT tokens
        localStorage.setItem('cyberguard_user', JSON.stringify(userData));
        localStorage.setItem('cyberguard_login_time', Date.now().toString());
        localStorage.setItem('token', data.tokens.access_token); // Regular login returns tokens.access_token
        localStorage.setItem('refresh_token', data.tokens.refresh_token); // Store refresh token

        setUser(userData);
        setIsAuthenticated(true);

        console.log('✅ Login successful:', userData.email, 'Tier:', userData.subscription_tier);
        return { success: true, user: userData };
      } else {
        const error = await response.json();
        console.error('❌ Login failed:', error.detail);
        return { success: false, error: error.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Network error - could not connect to server' };
    }
  };

  const refreshToken = async () => {
    try {
      const refresh_token = localStorage.getItem('refresh_token');
      if (!refresh_token) {
        throw new Error('No refresh token available');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';

      const response = await fetch(`${baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        console.log('✅ Token refreshed successfully');
        return data.access_token;
      } else {
        // Refresh failed, need to login again
        console.error('❌ Token refresh failed');
        logout();
        return null;
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      logout();
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('cyberguard_user');
    localStorage.removeItem('cyberguard_login_time');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    console.log('👋 User logged out');
  };

  const deleteAccount = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL || 'https://cyberguard-backend-wa00.onrender.com';
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        logout();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Account deletion failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error - could not connect to server' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    deleteAccount,
    checkAuthStatus,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};